/**
 * Dates are the tenant's wall clock. `service_date` goes to the API as `YYYY-MM-DDTHH:mm:ss`
 * with no offset and is built from strings — never through a `Date` — so a visitor in another
 * timezone can't shift it. Instants are only needed for the notice-period check and display.
 */
import type { BookingPolicy, DayAvailability, SpotsResponse } from '../api/types';

export interface WallClock {
  date: string; // YYYY-MM-DD
  hours: number;
  minutes: number;
}

const pad = (n: number) => String(n).padStart(2, '0');
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: string): boolean {
  return ISO_DATE.test(value);
}

export function formatServiceDate(date: string, hours: number, minutes: number): string {
  if (!isIsoDate(date)) throw new RangeError(`service date must be YYYY-MM-DD, got "${date}"`);
  return `${date}T${pad(hours)}:${pad(minutes)}:00`;
}

interface ZoneParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number; // 0 = Sunday
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function partsInZone(instant: Date, timeZone: string): ZoneParts {
  const parts: Record<string, string> = {};
  for (const p of formatter(timeZone).formatToParts(instant)) if (p.type !== 'literal') parts[p.type] = p.value;
  return {
    year: Number(parts['year']),
    month: Number(parts['month']),
    day: Number(parts['day']),
    hour: Number(parts['hour']) % 24,
    minute: Number(parts['minute']),
    second: Number(parts['second']),
    weekday: WEEKDAYS.indexOf(parts['weekday'] ?? ''),
  };
}

/** The tenant's current wall clock. */
export function tenantNow(timeZone: string, now: Date = new Date()): WallClock & { seconds: number; weekday: number } {
  const p = partsInZone(now, timeZone);
  return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, hours: p.hour, minutes: p.minute, seconds: p.second, weekday: p.weekday };
}

/** UTC offset of `timeZone` at `instant`, in ms (negative west of Greenwich). */
export function zoneOffsetMs(instant: Date, timeZone: string): number {
  const p = partsInZone(instant, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - instant.getTime() + instant.getMilliseconds();
}

/**
 * Wall clock in `timeZone` → instant. Ambiguous times (fall-back overlap) resolve to the first
 * occurrence; non-existent times (spring-forward gap) shift forward by the gap, like moment.
 */
export function wallClockToUtc(date: string, hours: number, minutes: number, timeZone: string): Date {
  if (!isIsoDate(date)) throw new RangeError(`date must be YYYY-MM-DD, got "${date}"`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const asUtc = Date.UTC(y, m - 1, d, hours, minutes, 0);
  const offset1 = zoneOffsetMs(new Date(asUtc), timeZone);
  let instant = asUtc - offset1;
  const offset2 = zoneOffsetMs(new Date(instant), timeZone);
  if (offset2 !== offset1) {
    const candidate = asUtc - offset2;
    const check = partsInZone(new Date(candidate), timeZone);
    if (check.hour === hours && check.minute === minutes) instant = candidate;
  }
  return new Date(instant);
}

/** `service_date` comes back from the API in UTC; a missing designator means UTC. */
export function parseUtcServiceDate(value: string): Date {
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return new Date(hasZone ? value : `${value}Z`);
}

const UNIT_MS: Record<string, number> = {
  minute: 60_000,
  minutes: 60_000,
  hour: 3_600_000,
  hours: 3_600_000,
  day: 86_400_000,
  days: 86_400_000,
};

export function noticePeriodMs(policy: BookingPolicy | null | undefined): number {
  if (!policy || !policy.quantity) return 0;
  return policy.quantity * (UNIT_MS[(policy.unit ?? 'hour').toLowerCase()] ?? UNIT_MS['hour']!);
}

/** True when the slot starts before `now + minimum notice` in the tenant's zone. */
export function isWithinNotice(slot: WallClock, policy: BookingPolicy | null | undefined, timeZone: string, now: Date): boolean {
  const instant = wallClockToUtc(slot.date, slot.hours, slot.minutes, timeZone);
  return instant.getTime() < now.getTime() + noticePeriodMs(policy);
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

export function formatTime(hours: number, minutes: number, timeFormat: '12' | '24' = '12'): string {
  if (timeFormat === '24') return `${pad(hours)}:${pad(minutes)}`;
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${pad(minutes)} ${hours < 12 ? 'AM' : 'PM'}`;
}

/**
 * The arrival window as a range, e.g. "8:00 – 9:00 AM" (60-minute window) — the tenant's own
 * copy calls these arrival windows, so show both ends. `arrival_window` is in minutes; 0 means
 * an exact time, which formats as a single time. The meridiem is printed once when both ends
 * share it ("8:00 – 9:00 AM") and twice when the window crosses noon or midnight
 * ("11:00 AM – 12:00 PM").
 */
export function formatTimeRange(hours: number, minutes: number, windowMinutes: number, timeFormat: '12' | '24' = '12'): string {
  const start = formatTime(hours, minutes, timeFormat);
  if (!windowMinutes || windowMinutes <= 0) return start;
  const total = hours * 60 + minutes + windowMinutes;
  const endHours = Math.floor(total / 60) % 24;
  const endMinutes = total % 60;
  const end = formatTime(endHours, endMinutes, timeFormat);
  if (timeFormat === '24') return `${start} – ${end}`;
  // Whole hours drop ":00" ("8 – 9 AM", "11 AM – 12 PM") — shorter, and it keeps the longest
  // window on one line on a phone. Half-hour slots keep their minutes ("8:30 – 9:30 AM").
  const whole = minutes === 0 && endMinutes === 0;
  const trim = (time: string) => (whole ? time.replace(':00', '') : time);
  const sameHalf = hours < 12 === endHours < 12;
  return sameHalf ? `${trim(start).replace(/ [AP]M$/, '')} – ${trim(end)}` : `${trim(start)} – ${trim(end)}`;
}

/** Display a YYYY-MM-DD without any timezone shift (formats it as a UTC midnight). */
export function formatDateLabel(date: string, locale = 'en-US', options: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }): string {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
}

export function formatInTenantTz(instant: Date, timeZone: string, locale = 'en-US'): string {
  return new Intl.DateTimeFormat(locale, { timeZone, dateStyle: 'full', timeStyle: 'short' }).format(instant);
}

export interface NormalizedSlot extends WallClock {
  arrivalWindow: number;
  available: boolean;
}

export interface DaySlots {
  date: string;
  holiday: boolean;
  slots: NormalizedSlot[];
}

/** Flattens both `/booking/spots` response shapes into one model with an `available` flag. */
export function slotsFromSpots(response: SpotsResponse): DaySlots[] {
  const days: DayAvailability[] = Array.isArray(response) ? response : response.days ?? [];
  return days
    .filter((d): d is DayAvailability & { date: string } => typeof d.date === 'string')
    .map((d) => ({
      date: d.date,
      holiday: d.holiday === true,
      slots: (d.spots ?? [])
        .filter((s) => typeof s.hours === 'number')
        .map((s) => ({
          date: d.date,
          hours: s.hours ?? 0,
          minutes: s.minutes ?? 0,
          arrivalWindow: s.arrival_window ?? 0,
          // The vendor's own widget treats free && !past as bookable; max_duration is 0 on tenants
          // with duration_for_spots off (both Chase tenants), so it must not gate availability.
          available: s.free === true && s.past !== true && s.closed_hours !== true,
        }))
        .sort((a, b) => a.hours - b.hours || a.minutes - b.minutes),
    }));
}
